/*
 * ototo-ds-header.js — opens and closes the menu panels.
 *
 * CLICK IS THE FLOOR, HOVER IS THE UPGRADE. The trigger is a real <button>
 * with aria-expanded and it opens on click, because that is the only gesture
 * every input device has. On a pointer that can hover — and only above the
 * burger breakpoint — the panel also opens when the item is pointed at, which
 * is the directive of 2026-09-14: "the drawer in the menu must open on hover".
 *
 * ⚠️ HOVER CANNOT BE DONE IN CSS HERE, AND THE OLD COMMENT CLAIMING IT WAS
 * LAYERED ON BEHIND (hover: hover) WAS WRONG — no such rule ever existed, which
 * is why nothing opened on hover. The panel's closed state is the `hidden`
 * ATTRIBUTE plus [hidden]{display:none !important}; a CSS :hover rule would
 * have to out-!important that on every panel variant while leaving
 * aria-expanded and the caret lying about the state. One code path, in script,
 * keeps the attribute, the caret and the assistive-tech state in agreement.
 *
 * ⚠️ HOVER NEEDS A GRACE PERIOD ON THE WAY OUT, NOT ON THE WAY IN. The panel is
 * a separate box below the trigger and the pointer crosses the 6px gap between
 * them; closing on the first `pointerleave` slams it shut mid-travel. Opening
 * is immediate — a delay there reads as lag.
 *
 * WITHOUT THIS FILE every top-level item that is a link still navigates, and
 * every panel stays closed. Nothing becomes unreachable; the dropdowns simply
 * stop opening.
 *
 * ⚠️ `hidden` IS ONLY A DEFAULT — any `display` rule out-specifies it. The CSS
 * declares [hidden]{display:none !important} against the panel's own class.
 */
(function () {
  'use strict';

  var header = document.querySelector('[data-ot-header]');
  if (!header) return;

  var triggers = [].slice.call(header.querySelectorAll('[data-ot-menu-trigger]'));

  /* Below 1099px the nav is the burger drawer: every panel is static inside a
     stacked list and there is no hover to speak of. Checked at event time, not
     at load, so a resized window behaves. */
  var fine = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  function hoverable() {
    return !!(fine && fine.matches) && window.innerWidth >= 1100;
  }

  function panelOf(trigger) {
    return document.getElementById(trigger.getAttribute('aria-controls'));
  }

  /* ── the promo tile's video ───────────────────────────────────────────────
     Nothing is fetched until a panel is first opened: the address sits in
     `data-src` and these files run to megabytes, on a header that is on every
     page of the store. Same contract as assets/ototo-reels.js — the poster
     image is the floor and the video is the upgrade, so `is-playing` is set
     from the `playing` event and never optimistically. A file that 404s, is
     still transcoding, or is refused autoplay leaves the still showing. */
  function media(panel, on) {
    if (!panel) return;
    [].slice.call(panel.querySelectorAll('[data-ot-promo-video]')).forEach(function (v) {
      if (!on) { v.pause(); return; }
      if (!v.dataset.loaded) {
        var src = v.getAttribute('data-src');
        if (!src) return;
        v.muted = true; /* Safari refuses play() on a video it thinks is unmuted. */
        v.src = src;
        v.dataset.loaded = '1';
        v.addEventListener('playing', function () { v.classList.add('is-playing'); });
        v.addEventListener('error', function () { v.classList.remove('is-playing'); });
      }
      var p = v.play();
      if (p && p.catch) p.catch(function () { /* autoplay declined — the still stays */ });
    });
  }

  function open(trigger) {
    closeAll(trigger);
    trigger.setAttribute('aria-expanded', 'true');
    var panel = panelOf(trigger);
    if (!panel) return;
    panel.removeAttribute('hidden');
    media(panel, true);
  }

  function close(trigger) {
    trigger.setAttribute('aria-expanded', 'false');
    var panel = panelOf(trigger);
    if (!panel) return;
    panel.setAttribute('hidden', '');
    media(panel, false);
  }

  function closeAll(except) {
    triggers.forEach(function (t) {
      if (t === except) return;
      close(t);
    });
  }

  function isOpen(trigger) {
    return trigger.getAttribute('aria-expanded') === 'true';
  }

  triggers.forEach(function (trigger) {
    trigger.addEventListener('click', function () {
      if (isOpen(trigger)) close(trigger);
      else open(trigger);
    });
  });

  /* ── hover ───────────────────────────────────────────────────────────────
     Bound to the <li>, not the button: the panel is inside the item, so one
     element covers both the trigger and the box it opens and the pointer never
     leaves the element it entered. `pointerenter`/`pointerleave` rather than
     `mouseenter` so a pen or a hybrid device is described honestly; the
     `hoverable()` guard is what keeps a touch tap from opening and instantly
     closing. */
  var shut = null;
  function cancelShut() {
    if (shut) { clearTimeout(shut); shut = null; }
  }

  triggers.forEach(function (trigger) {
    var item = trigger.closest('.ot-header__item');
    if (!item) return;

    item.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'touch' || !hoverable()) return;
      cancelShut();
      if (!isOpen(trigger)) open(trigger);
    });

    item.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'touch' || !hoverable()) return;
      cancelShut();
      /* 180ms is the gap between the trigger and the panel, crossed at a
         normal pointer speed, with room to overshoot. */
      shut = setTimeout(function () { close(trigger); }, 180);
    });
  });

  /* A mega panel is `position: static` on its item and spans the page, so the
     pointer can travel from one item's panel onto a neighbouring item. Keeping
     the whole header alive while the pointer is anywhere in it stops that
     handing off into a flicker. */
  header.addEventListener('pointerenter', cancelShut);

  /* A click anywhere else closes. Escape closes and returns focus to the
     trigger, which is what a keyboard user expects from a disclosure. */
  document.addEventListener('click', function (e) {
    if (!header.contains(e.target)) closeAll(null);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var was = triggers.filter(isOpen)[0];
    closeAll(null);
    if (was) was.focus();
  });

  /* Keyboard focus is not hover, and it must still open the panel for anyone
     tabbing through — but only leaving the header entirely closes it, or the
     first Tab into the panel would shut the thing being entered. */
  header.addEventListener('focusout', function (e) {
    if (e.relatedTarget && header.contains(e.relatedTarget)) return;
    closeAll(null);
  });

  /* ── the phone menu drawer ───────────────────────────────────────────────
     The burger used to reveal the desktop nav as a stacked list under the bar.
     It now opens a real drawer — see the block at the end of ds-header.liquid.

     ⚠️ THE DRAWER IS NOT INSIDE `header`. It is a sibling, because the header
     is a sticky stacking context and a fixed panel inside one is trapped in it.
     So it is found on the DOCUMENT, not on `header`, and every listener below
     has to be too.

     ⚠️ THE SCRIM AND THE PANEL ARE HIDDEN AND SHOWN IN TWO STEPS. `hidden` has
     to come off before the transform can animate, and the class has to go on a
     later frame or the browser coalesces both into the end state and the panel
     appears instead of sliding. Two rAFs, the same as the cart drawer. */
  var burger = header.querySelector('[data-ot-burger]');
  var panel = document.querySelector('[data-ot-mdrawer]');
  var scrim = document.querySelector('.ot-mdrawer__scrim');

  if (burger && panel) {
    var lastFocus = null;

    var openDrawer = function () {
      lastFocus = document.activeElement;
      closeAll(null);
      panel.removeAttribute('hidden');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          panel.classList.add('is-open');
          if (scrim) scrim.classList.add('is-open');
        });
      });
      burger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('ot-drawer-open');
      var first = panel.querySelector('[data-ot-mdrawer-close]');
      if (first && first.focus) first.focus();
    };

    var closeDrawer = function () {
      panel.classList.remove('is-open');
      if (scrim) scrim.classList.remove('is-open');
      burger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('ot-drawer-open');
      /* Wait for the slide-out before hiding, or it vanishes instead of
         leaving. A transitionend listener never fires when reduced motion
         collapses the duration, which is why this is a timer. */
      window.setTimeout(function () { panel.setAttribute('hidden', ''); }, 350);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    };

    burger.addEventListener('click', function () {
      if (burger.getAttribute('aria-expanded') === 'true') closeDrawer();
      else openDrawer();
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-ot-mdrawer-close]')) {
        e.preventDefault();
        closeDrawer();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (panel.hasAttribute('hidden')) return;
      if (e.key === 'Escape') { closeDrawer(); return; }
      /* aria-modal: Tab cycles inside the drawer instead of walking into the
         page behind it (go-live 8.4, 2026-10-13). Same rule as ototo-cart.js. */
      if (e.key !== 'Tab') return;
      var f = [].slice.call(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (!panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    /* Following a link inside the drawer navigates; leaving the panel open
       behind the new page is what a back button then lands on. */
    panel.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a[href]');
      if (link && link.getAttribute('href') && link.getAttribute('href').charAt(0) !== '#') closeDrawer();
    });

    /* A native <select> in the locale row: submitting its form is what actually
       changes language or market. Dawn ships JS for this; a plain listener is
       all it needs. */
    panel.querySelectorAll('[data-ot-locale-submit]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        if (sel.form) sel.form.submit();
      });
    });

    /* Resizing up to the desktop bar must not leave a drawer open off-screen. */
    window.addEventListener('resize', function () {
      if (window.innerWidth >= 1100 && burger.getAttribute('aria-expanded') === 'true') closeDrawer();
    });
  }

  /* ── the everywhere search overlay ─────────────────────────────────────────
     The bar's search icon is a toggle; this drops the predictive-search panel
     under the header on every page. Dawn's predictive-search.js owns the results;
     this only shows/hides the panel and puts focus in the field.

     ⚠️ THE PANEL IS INSIDE `header`, so a click within it is a click within the
     header and the nav's own outside-click closer leaves it alone. Escape, the
     close button, and a pointerdown outside both the panel and its toggle close
     it. */
  var searchToggle = header.querySelector('[data-ot-search-toggle]');
  var searchPanel = header.querySelector('[data-ot-search]');
  if (searchToggle && searchPanel) {
    var searchInput = searchPanel.querySelector('#ot-search-input');

    var openSearch = function () {
      closeAll(null);
      searchPanel.removeAttribute('hidden');
      searchToggle.setAttribute('aria-expanded', 'true');
      if (searchInput && searchInput.focus) {
        requestAnimationFrame(function () { searchInput.focus(); });
      }
    };

    var closeSearch = function () {
      if (searchPanel.hasAttribute('hidden')) return;
      var hadFocus = searchPanel.contains(document.activeElement);
      searchPanel.setAttribute('hidden', '');
      searchToggle.setAttribute('aria-expanded', 'false');
      /* Focus was inside the panel that just vanished: hand it back to the
         toggle, or it falls to <body> (go-live 8.4). */
      if (hadFocus) searchToggle.focus();
    };

    searchToggle.addEventListener('click', function () {
      if (searchPanel.hasAttribute('hidden')) openSearch();
      else closeSearch();
    });

    [].slice.call(searchPanel.querySelectorAll('[data-ot-search-close]')).forEach(function (b) {
      b.addEventListener('click', closeSearch);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeSearch();
    });

    /* Outside-click: pointerdown so it lands before focus moves. Ignore the
       toggle (its own handler flips state) and anything inside the panel. */
    document.addEventListener('pointerdown', function (e) {
      if (searchPanel.hasAttribute('hidden')) return;
      if (searchPanel.contains(e.target) || searchToggle.contains(e.target)) return;
      closeSearch();
    });
  }

  /* ── overlay header: transparent over the hero, solid on scroll ─────────────
     Only present on the homepage (ds-header adds .ot-header--overlay there). The
     hero is lifted under the bar by a negative margin on the header GROUP whose
     size is this bar's measured height — published on :root so it inherits UP to
     the group wrapper (a var set on the header would not reach an ancestor). */
  if (header.classList.contains('ot-header--overlay')) {
    /* pinAt = the header's document offset = the announcement-bar height. Turning
       white and switching to `fixed` exactly here is seamless: the in-flow bar has
       just reached y=0, so the fixed bar lands in the same place. Measured only
       while not pinned (once fixed, the header's own top is 0, not the offset). */
    var pinAt = 1;
    var measure = function () {
      document.documentElement.style.setProperty('--ot-overlay-pull', header.offsetHeight + 'px');
      if (!header.classList.contains('is-scrolled')) {
        pinAt = Math.max(1, Math.round(header.getBoundingClientRect().top + window.scrollY));
      }
    };
    var onHeaderScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY >= pinAt);
    };
    measure();
    onHeaderScroll();
    window.addEventListener('resize', function () { measure(); onHeaderScroll(); });
    window.addEventListener('scroll', onHeaderScroll, { passive: true });
  }
})();
